import React, { useState, useRef } from 'react';
import Modal from './Modal';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faSpinner } from '@fortawesome/free-solid-svg-icons';

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
        return centerCrop(
                makeAspectCrop(
                        {
                                unit: '%',
                                width: 90,
                        },
                        aspect,
                        mediaWidth,
                        mediaHeight
                ),
                mediaWidth,
                mediaHeight
        );
}

const ImageUploadModal = ({ isOpen, onClose, onSave }) => {
        const [imgSrc, setImgSrc] = useState('');
        const [crop, setCrop] = useState();
        const [completedCrop, setCompletedCrop] = useState();
        const [isDragging, setIsDragging] = useState(false);
        const [isSaving, setIsSaving] = useState(false);
        const [error, setError] = useState('');
        const imgRef = useRef(null);
        const previewCanvasRef = useRef(null);
        const fileInputRef = useRef(null);
        const aspect = 1;
        const MAX_SIZE = 10 * 1024 * 1024; // 10MB

        const handleFile = (file) => {
                setError('');
                if (!file || !['image/jpeg', 'image/png'].includes(file.type)) {
                        setError('Please select a JPEG or PNG image.');
                        return;
                }
                if (file.size > MAX_SIZE) {
                        setError('File is too large. Maximum size is 10MB.');
                        return;
                }
                setCrop(undefined);
                const reader = new FileReader();
                reader.addEventListener('load', () =>
                        setImgSrc(reader.result.toString() || '')
                );
                reader.readAsDataURL(file);
        };

        const onSelectFile = (e) => {
                if (e.target.files && e.target.files.length > 0) {
                        handleFile(e.target.files[0]);
                }
        };

        const onDragOver = (e) => {
                e.preventDefault();
                setIsDragging(true);
        };

        const onDragLeave = (e) => {
                e.preventDefault();
                setIsDragging(false);
        };

        const onDrop = (e) => {
                e.preventDefault();
                setIsDragging(false);
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                        handleFile(e.dataTransfer.files[0]);
                }
        };

        const onImageLoad = (e) => {
                const { width, height } = e.currentTarget;
                setCrop(centerAspectCrop(width, height, aspect));
        };

        // Render the cropped selection into the final 1280x1280 PNG blob we upload.
        const buildFinalBlob = async () => {
                await canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
                const croppedBlob = await new Promise(resolve => previewCanvasRef.current.toBlob(resolve, 'image/png'));
                if (!croppedBlob) return null;

                const image = await createImageBitmap(croppedBlob);
                const canvas = document.createElement('canvas');
                canvas.width = 1280;
                canvas.height = 1280;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(image, 0, 0, 1280, 1280);
                return await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        };

        const handleSave = async () => {
                if (!(completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current)) {
                        return;
                }

                // Build the blob first, while the crop canvas/img are still mounted —
                // flipping isSaving unmounts them and would null the refs.
                const finalBlob = await buildFinalBlob();
                if (!finalBlob) {
                        return;
                }

                setIsSaving(true);
                const startedAt = Date.now();
                let result = { ok: false, notify: null };
                try {
                        // onSave uploads the image and queries Pl@ntNet; it resolves to
                        // { ok, notify } where notify() shows the result toast.
                        result = await onSave(finalBlob);
                } finally {
                        // Keep the "identifying" state visible for at least 4s, even when the
                        // upload/identification finishes faster.
                        const elapsed = Date.now() - startedAt;
                        if (elapsed < 4000) {
                                await new Promise(resolve => setTimeout(resolve, 4000 - elapsed));
                        }
                        setIsSaving(false);
                        if (result?.ok) {
                                setImgSrc('');
                                onClose();
                        }
                        // Show the toast only after the overlay closes, not during it.
                        result?.notify?.();
                }
        };

        async function canvasPreview(image, canvas, crop) {
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                        throw new Error('No 2d context');
                }

                const scaleX = image.naturalWidth / image.width;
                const scaleY = image.naturalHeight / image.height;
                const pixelRatio = window.devicePixelRatio;

                canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
                canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

                ctx.scale(pixelRatio, pixelRatio);
                ctx.imageSmoothingQuality = 'high';

                const cropX = crop.x * scaleX;
                const cropY = crop.y * scaleY;

                const centerX = image.naturalWidth / 2;
                const centerY = image.naturalHeight / 2;

                ctx.save();
                ctx.translate(-cropX, -cropY);
                ctx.translate(centerX, centerY);
                ctx.translate(-centerX, -centerY);
                ctx.drawImage(
                        image,
                        0,
                        0,
                        image.naturalWidth,
                        image.naturalHeight,
                        0,
                        0,
                        image.naturalWidth,
                        image.naturalHeight
                );
                ctx.restore();
        }

        if (isSaving) {
                return (
                        <Modal isOpen={isOpen} onClose={() => {}}>
                                <div className="p-6 flex flex-col items-center justify-center py-10">
                                        <FontAwesomeIcon icon={faSpinner} spin className="text-brand text-4xl mb-4" />
                                        <p className="text-gray-700 font-medium">We're trying to identify the plant…</p>
                                        <p className="text-gray-400 text-sm mt-1">This can take a few seconds.</p>
                                </div>
                        </Modal>
                );
        }

        return (
                <Modal isOpen={isOpen} onClose={onClose}>
                        <div className="p-6">
                                <h3 className="text-lg font-bold mb-4">Upload and Crop Image</h3>
                                <div className="flex flex-col items-center">
                                        {!imgSrc ? (
                                                <>
                                                        <input
                                                                ref={fileInputRef}
                                                                type="file"
                                                                accept="image/jpeg,image/png"
                                                                data-testid="image-file-input"
                                                                onChange={onSelectFile}
                                                                className="hidden"
                                                        />
                                                        <button
                                                                type="button"
                                                                onClick={() => fileInputRef.current?.click()}
                                                                data-testid="image-choose"
                                                                onDragOver={onDragOver}
                                                                onDragLeave={onDragLeave}
                                                                onDrop={onDrop}
                                                                className={`w-full p-8 border-2 border-dashed rounded-xl text-center transition-colors cursor-pointer ${isDragging ? 'border-brand bg-brand/5' : 'border-gray-300 hover:border-brand hover:bg-gray-50'}`}
                                                        >
                                                                <FontAwesomeIcon icon={faUpload} className="text-gray-400 text-3xl mb-3" />
                                                                <p className="text-gray-600 font-medium">Click or drag to upload an image</p>
                                                                <p className="text-gray-400 text-sm mt-1">JPEG or PNG up to 10MB</p>
                                                        </button>
                                                        {error && (
                                                                <p className="text-red-500 text-sm mt-2">{error}</p>
                                                        )}
                                                </>
                                        ) : (
                                                <>
                                                        <ReactCrop
                                                                crop={crop}
                                                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                                                onComplete={(c) => setCompletedCrop(c)}
                                                                aspect={aspect}
                                                        >
                                                                <img
                                                                        ref={imgRef}
                                                                        alt="Crop me"
                                                                        src={imgSrc}
                                                                        onLoad={onImageLoad}
                                                                        style={{ maxHeight: '70vh' }}
                                                                />
                                                        </ReactCrop>
                                                        <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
                                                        <div className="mt-6 flex justify-end space-x-4 w-full">
                                                                <button
                                                                        onClick={handleSave}
                                                                        data-testid="image-save"
                                                                        className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm disabled:bg-gray-300"
                                                                        disabled={!completedCrop?.width || !completedCrop?.height}
                                                                >
                                                                        Save
                                                                </button>
                                                                <button
                                                                        onClick={() => setImgSrc('')}
                                                                        data-testid="image-reset"
                                                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                                                >
                                                                        Choose Another
                                                                </button>
                                                                <button
                                                                        onClick={onClose}
                                                                        data-testid="image-cancel"
                                                                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:text-sm"
                                                                >
                                                                        Cancel
                                                                </button>
                                                        </div>
                                                </>
                                        )}
                                </div>
                        </div>
                </Modal>
        );
};

export default ImageUploadModal;
